const MODIFIER_ORDER = ['Ctrl', 'Alt', 'Shift'] as const;

const DISPLAY_KEY_ALIASES: Record<string, string> = {
  ' ': 'Space',
  escape: 'Esc',
  arrowup: 'Up',
  arrowdown: 'Down',
  arrowleft: 'Left',
  arrowright: 'Right',
};

const IGNORED_MAIN_KEYS = new Set(['control', 'shift', 'alt', 'meta']);

/** Arabic presentation forms + main Arabic blocks (letters/digits common in UI). */
const ARABIC_SCRIPT_PATTERN =
  /[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF]/;

export interface CapturedShortcut {
  display: string;
  normalized: string;
}

/** Removes invisible bidi / format chars that sometimes appear in API or RTL copy-paste. */
export function sanitizeShortcutKeysLabel(value: string): string {
  return value
    .replace(/[\u200e\u200f\u2066\u2067\u2068\u2069\ufeff]/g, '')
    .replace(/\uFF0B/g, '+')
    .trim();
}

/** Case-insensitive lookup key (matches settings duplicate validation). */
export function toCompactShortcutKey(value: string): string {
  return normalizeShortcutDisplay(value).replace(/\s+/g, '').toUpperCase();
}

/** Display + compact keys used to match API labels and live keyboard input. */
export function buildShortcutLookupKeys(keys: string): string[] {
  const normalized = normalizeShortcutDisplay(keys);
  if (!normalized) {
    return [];
  }

  const compact = toCompactShortcutKey(normalized);
  return compact === normalized ? [normalized] : [normalized, compact];
}

export function normalizeShortcutDisplay(value: string): string {
  const tokens = sanitizeShortcutKeysLabel(value)
    .split('+')
    .map((token) => token.trim())
    .filter(Boolean);

  const normalizedModifiers = new Set<string>();
  let mainKey = '';

  for (const token of tokens) {
    const lowerToken = token.toLowerCase();
    if (lowerToken === 'meta' || lowerToken === 'cmd' || lowerToken === 'command') {
      normalizedModifiers.add('Ctrl');
      continue;
    }

    if (lowerToken === 'ctrl' || lowerToken === 'control') {
      normalizedModifiers.add('Ctrl');
      continue;
    }

    if (lowerToken === 'alt' || lowerToken === 'option') {
      normalizedModifiers.add('Alt');
      continue;
    }

    if (lowerToken === 'shift') {
      normalizedModifiers.add('Shift');
      continue;
    }

    mainKey = formatMainKey(token);
  }

  const orderedModifiers = MODIFIER_ORDER.filter((modifier) => normalizedModifiers.has(modifier));
  return buildShortcutString(orderedModifiers, mainKey);
}

export function captureShortcutFromKeyboardEvent(event: KeyboardEvent): CapturedShortcut | null {
  if (event.key === 'Tab') {
    return null;
  }

  const keyLower = event.key.toLowerCase();
  if (IGNORED_MAIN_KEYS.has(keyLower)) {
    return null;
  }

  const hasNonShiftModifier = event.ctrlKey || event.altKey || event.metaKey;
  if (!hasNonShiftModifier && (keyLower === 'backspace' || keyLower === 'delete')) {
    return null;
  }

  const modifiers = new Set<string>();
  if (event.ctrlKey || event.metaKey) {
    modifiers.add('Ctrl');
  }
  if (event.altKey) {
    modifiers.add('Alt');
  }
  if (event.shiftKey) {
    modifiers.add('Shift');
  }

  const resolvedMainKey = resolveMainKeyLabel(event);
  if (!resolvedMainKey) {
    return null;
  }

  const mainKey = formatMainKey(resolvedMainKey);
  if (!mainKey) {
    return null;
  }

  if (containsArabicScript(mainKey)) {
    return null;
  }

  const orderedModifiers = MODIFIER_ORDER.filter((modifier) => modifiers.has(modifier));
  const display = buildShortcutString(orderedModifiers, mainKey);
  if (!display) {
    return null;
  }

  return {
    display,
    normalized: normalizeShortcutDisplay(display),
  };
}

function resolveMainKeyLabel(event: KeyboardEvent): string | null {
  const rawKey = event.key;
  if (typeof rawKey === 'string') {
    const trimmedKey = rawKey.trim();
    const lowerKey = trimmedKey.toLowerCase();
    const isUnusableKey =
      trimmedKey === '' ||
      lowerKey === 'unidentified' ||
      lowerKey === 'process' ||
      lowerKey === 'dead';

    if (!isUnusableKey) {
      if (containsArabicScript(trimmedKey)) {
        return mapCodeToMainKeyLabel(event.code);
      }
      return trimmedKey;
    }
  }

  return mapCodeToMainKeyLabel(event.code);
}

function containsArabicScript(value: string): boolean {
  return ARABIC_SCRIPT_PATTERN.test(value);
}

function mapCodeToMainKeyLabel(code: string): string | null {
  if (!code) {
    return null;
  }

  const digitMatch = /^Digit(\d)$/.exec(code);
  if (digitMatch) {
    return digitMatch[1];
  }

  const numpadDigitMatch = /^Numpad(\d)$/.exec(code);
  if (numpadDigitMatch) {
    return `Num${numpadDigitMatch[1]}`;
  }

  const keyLetterMatch = /^Key([A-Z])$/i.exec(code);
  if (keyLetterMatch) {
    return keyLetterMatch[1].toUpperCase();
  }

  const fnMatch = /^F(\d{1,2})$/i.exec(code);
  if (fnMatch) {
    return `F${fnMatch[1]}`;
  }

  const codeAliases: Record<string, string> = {
    Space: ' ',
    Minus: '-',
    Equal: '=',
    BracketLeft: '[',
    BracketRight: ']',
    Backslash: '\\',
    Semicolon: ';',
    Quote: "'",
    Comma: ',',
    Period: '.',
    Slash: '/',
    Backquote: '`',
    IntlBackslash: '\\',
    NumpadAdd: '+',
    NumpadSubtract: '-',
    NumpadMultiply: '*',
    NumpadDivide: '/',
    NumpadDecimal: '.',
  };

  return codeAliases[code] ?? null;
}

function buildShortcutString(modifiers: string[], mainKey: string): string {
  if (!mainKey) {
    return '';
  }

  const parts = [...modifiers, mainKey];
  return parts.join(' + ');
}

function formatMainKey(value: string): string {
  const trimmed = value.trim();
  if (!trimmed) {
    return '';
  }

  const lowerValue = trimmed.toLowerCase();
  if (DISPLAY_KEY_ALIASES[lowerValue]) {
    return DISPLAY_KEY_ALIASES[lowerValue];
  }

  if (/^f\d{1,2}$/i.test(trimmed)) {
    return trimmed.toUpperCase();
  }

  if (trimmed.length === 1) {
    return trimmed.toUpperCase();
  }

  return `${trimmed.charAt(0).toUpperCase()}${trimmed.slice(1).toLowerCase()}`;
}
